import { bookingState } from './BookingState';
import { refreshBooking } from './BookingManager';
import { get } from 'svelte/store';

export class AuthManager {
    static tokenCheckInterval = null;
    static loginWindow = null;
    static refreshTokenInterval = null;
    static tokenRefreshInProgress = false;
    static REQUIRED_VERSION = '1.2'; // The minimum version required (v2.0 = extension)
    static versionAlreadyChecked = false; // Track if version has been checked

    static async waitForUserscript(maxAttempts = 5) {
        for (let i = 0; i < maxAttempts; i++) {
            if (typeof window.retrieveCommunautoTokens === 'function') {
                // Check script version
                await this.checkScriptVersion();

                // Load tokens immediately when userscript is detected
                await this.loadTokens();
                // Start refresh token check interval
                this.startRefreshTokenCheck();
                return true;
            }
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        return false;
    }

    static async checkScriptVersion() {
        // Skip if already checked
        if (this.versionAlreadyChecked) {
            return true;
        }

        try {
            // Check if the version function exists
            if (typeof window.getBridgeVersion === 'function') {
                const currentVersion = window.getBridgeVersion();

                this.versionAlreadyChecked = true; // Mark as checked

                // Compare with required version using semantic versioning rules
                const isOutdated = this.isVersionOutdated(currentVersion, this.REQUIRED_VERSION);

                if (isOutdated) {
                    // Show upgrade notification
                    this.showVersionUpdateNotification(currentVersion, this.REQUIRED_VERSION);

                    if (typeof window.plausible === 'function') {
                        window.plausible('Outdated Script Version', {
                            props: {
                                currentVersion,
                                requiredVersion: this.REQUIRED_VERSION
                            }
                        });
                    }

                    return false;
                }

                return true;
            } else {
                // Assume old version if getBridgeVersion function doesn't exist
                this.showVersionUpdateNotification('unknown', this.REQUIRED_VERSION);
                this.versionAlreadyChecked = true; // Mark as checked
                return false;
            }
        } catch (error) {
            this.versionAlreadyChecked = true; // Mark as checked to prevent repeated errors
            return false;
        }
    }

    static isVersionOutdated(currentVersion, requiredVersion) {
        // Simple semantic version comparison
        const current = currentVersion.split('.').map(Number);
        const required = requiredVersion.split('.').map(Number);

        for (let i = 0; i < Math.max(current.length, required.length); i++) {
            const a = current[i] || 0;
            const b = required[i] || 0;
            if (a < b) return true;
            if (a > b) return false;
        }

        return false; // Versions are equal
    }

    static showVersionUpdateNotification(currentVersion, requiredVersion) {
        // Create banner notification
        const banner = document.createElement('div');
        banner.id = 'version-update-banner';
        banner.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background-color: #f39c12;
            color: white;
            padding: 12px 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            z-index: 9999;
            font-size: 14px;
        `;

        // Banner content container (left side)
        const contentContainer = document.createElement('div');
        contentContainer.style.cssText = `
            display: flex;
            align-items: center;
            gap: 12px;
        `;

        // Warning icon
        const icon = document.createElement('span');
        icon.innerHTML = '⚠️';
        icon.style.cssText = `
            font-size: 18px;
        `;

        // Determine language for notification text
        const language = localStorage.getItem('language') || 'FR';

        // Message text with language support
        const message = document.createElement('span');
        const messageText = language === 'FR'
            ? `<strong>Mise à jour requise :</strong> Votre script Communauto Token Bridge (v${currentVersion}) doit être mis à jour vers la v${requiredVersion}.`
            : `<strong>Update Required:</strong> Your Communauto Token Bridge script (v${currentVersion}) needs to be updated to v${requiredVersion}.`;

        message.innerHTML = messageText;

        // Action container (right side)
        const actionContainer = document.createElement('div');
        actionContainer.style.cssText = `
            display: flex;
            align-items: center;
            gap: 16px;
        `;

        // Update button with language support
        const updateButton = document.createElement('a');
        updateButton.style.cssText = `
            background-color: white;
            color: #e67e22;
            padding: 6px 12px;
            border-radius: 4px;
            text-decoration: none;
            font-weight: bold;
            cursor: pointer;
            transition: background-color 0.2s;
        `;
        updateButton.textContent = language === 'FR' ? 'Mettre à jour' : 'Update Now';
        updateButton.href = 'https://update.greasyfork.org/scripts/530915/Communauto%20Token%20Bridge.user.js';
        updateButton.target = '_blank';
        updateButton.onmouseover = function() {
            this.style.backgroundColor = '#f5f5f5';
        };
        updateButton.onmouseout = function() {
            this.style.backgroundColor = 'white';
        };

        // Close button
        const closeButton = document.createElement('button');
        closeButton.style.cssText = `
            background: none;
            border: none;
            color: white;
            cursor: pointer;
            font-size: 16px;
            opacity: 0.8;
            transition: opacity 0.2s;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
        `;
        closeButton.innerHTML = '×';
        closeButton.title = language === 'FR' ? 'Fermer' : 'Dismiss';
        closeButton.onmouseover = function() {
            this.style.opacity = '1';
        };
        closeButton.onmouseout = function() {
            this.style.opacity = '0.8';
        };
        closeButton.onclick = function() {
            document.body.removeChild(banner);
        };

        // Assemble the banner
        contentContainer.appendChild(icon);
        contentContainer.appendChild(message);
        actionContainer.appendChild(updateButton);
        actionContainer.appendChild(closeButton);
        banner.appendChild(contentContainer);
        banner.appendChild(actionContainer);

        // Add to body
        document.body.appendChild(banner);

    }

    static async loadTokens() {
        try {
            const tokens = await window.retrieveCommunautoTokens();
            if (tokens) {
                // Normalize expires_at: TM v1.2 stores seconds, extension stores ms
                let expiresAt = tokens.expires_at;
                if (expiresAt && expiresAt < 10000000000) {
                    expiresAt = expiresAt * 1000; // seconds -> ms
                }
                bookingState.update(state => ({
                    ...state,
                    token: tokens.access_token,
                    expiresAt: expiresAt,
                }));
                if (typeof window.plausible === 'function') {
                    window.plausible('Auth Success');
                }
                return true;
            }
        } catch (error) {
            if (typeof window.plausible === 'function') {
                window.plausible('Auth Error', { props: { error: error.message } });
            }
        }
        return false;
    }

    static isTokenExpired(expiresAt) {
        if (!expiresAt) return true;
        // Consider expired 60 seconds early to avoid edge cases
        return Date.now() >= (expiresAt - 60000);
    }

    static async getValidAccessToken() {
        const tokens = await window.retrieveCommunautoTokens();

        if (!tokens) {
            console.error('[AuthManager] No tokens available, please log in');
            return null;
        }

        // Check if token is expired
        if (this.isTokenExpired(tokens.expires_at)) {
            console.log('[AuthManager] Access token expired, refreshing...');
            await this.refreshAccessToken();
            // Get the refreshed token
            const refreshedTokens = await window.retrieveCommunautoTokens();
            return refreshedTokens?.access_token || null;
        }

        return tokens.access_token;
    }

    static async startTokenPolling() {
        if (this.tokenCheckInterval) clearInterval(this.tokenCheckInterval);

        this.tokenCheckInterval = setInterval(async () => {
            const tokens = await window.retrieveCommunautoTokens();
            if (tokens) {
                // Normalize expires_at: TM v1.2 stores seconds, extension stores ms
                let expiresAt = tokens.expires_at;
                if (expiresAt && expiresAt < 10000000000) {
                    expiresAt = expiresAt * 1000;
                }
                bookingState.update(state => ({
                    ...state,
                    token: tokens.access_token,
                    expiresAt: expiresAt,
                }));
                if (typeof window.acknowledgeCommunautoTokens === 'function') {
                    window.acknowledgeCommunautoTokens();
                }
                await refreshBooking();
                if (this.loginWindow) {
                    this.loginWindow.close();
                }
                clearInterval(this.tokenCheckInterval);
            }
        }, 2000);

        // Clear interval after 5 minutes
        setTimeout(() => {
            if (this.tokenCheckInterval) {
                clearInterval(this.tokenCheckInterval);
                this.tokenCheckInterval = null;
            }
        }, 5 * 60 * 1000);
    }

    static async logout() {
        console.log('[AuthManager] Logging out...');

        // Clear tokens from bridge storage (TM or extension)
        if (typeof window.clearCommunautoTokens === 'function') {
            try {
                await window.clearCommunautoTokens();
                console.log('[AuthManager] Bridge tokens cleared');
            } catch (e) {
                console.error('[AuthManager] Error clearing bridge tokens:', e);
            }
        }

        // Clear Flexfinder state
        bookingState.set({
            booking: null,
            customerId: null,
            token: null,
            expiresAt: null,
        });

        // Clear all intervals
        if (this.tokenCheckInterval) {
            clearInterval(this.tokenCheckInterval);
            this.tokenCheckInterval = null;
        }
        if (this.refreshTokenInterval) {
            clearInterval(this.refreshTokenInterval);
            this.refreshTokenInterval = null;
        }

        // Close any open login window
        if (this.loginWindow) {
            this.loginWindow.close();
            this.loginWindow = null;
        }

        console.log('[AuthManager] Logged out');
    }

    static async openCommunauto() {
        console.log("opening communauto with mobile OAuth")

        // Use mobile OAuth if the function is available
        if (typeof window.buildMobileAuthUrl === 'function') {
            try {
                const authUrl = await window.buildMobileAuthUrl();
                this.loginWindow = window.open(authUrl, '_blank', 'width=600,height=800');
                this.startTokenPolling();
                console.log('[AuthManager] Mobile OAuth flow started');
            } catch (error) {
                console.error('[AuthManager] Error building mobile auth URL:', error);
                // Fallback to web OAuth
                this.loginWindow = window.open('https://quebec.client.reservauto.net', '_blank');
                this.startTokenPolling();
            }
        } else {
            // Fallback to legacy web OAuth
            console.log('[AuthManager] Falling back to web OAuth (userscript not detected)');
            this.loginWindow = window.open('https://quebec.client.reservauto.net', '_blank');
            this.startTokenPolling();
        }
    }

    static startRefreshTokenCheck() {
        if (this.refreshTokenInterval) clearInterval(this.refreshTokenInterval);

        // Check token expiration every minute
        this.refreshTokenInterval = setInterval(async () => {
            await this.checkAndRefreshToken();
        }, 60 * 1000);
    }

    static async checkAndRefreshToken() {
        // Don't run multiple refreshes simultaneously
        if (this.tokenRefreshInProgress) return;

        try {
            const state = get(bookingState);
            const { token, expiresAt } = state;

            // If no token or already expired, try refresh
            if (!token || !expiresAt) {
                await this.refreshAccessToken();
                return;
            }

            // Check if token will expire in the next 5 minutes (expiresAt is in ms)
            const expiryTime = expiresAt;
            const currentTime = Date.now();
            const timeUntilExpiry = expiryTime - currentTime;

            // If token expires in less than 5 minutes, refresh it
            if (timeUntilExpiry < 5 * 60 * 1000) {
                await this.refreshAccessToken();
            }
        } catch (error) {
            // Error checking token expiration
        }
    }

    static async refreshAccessToken() {
        if (this.tokenRefreshInProgress) return false;
        this.tokenRefreshInProgress = true;

        try {
            console.log('Attempting to refresh access token');

            // First try: Use TamperMonkey's refresh function (bypasses CORS, uses mobile OAuth)
            if (typeof window.refreshCommunautoToken === 'function') {
                try {
                    const newTokens = await window.refreshCommunautoToken();
                    if (newTokens && newTokens.access_token) {
                        console.log('[AuthManager] Token refreshed via userscript (mobile OAuth)');

                        // Calculate expires_at in ms (consistent with extension storage and bookingState)
                        const expiresAt = Date.now() + (newTokens.expires_in * 1000);
                        newTokens.expires_at = expiresAt;

                        // Update booking state
                        bookingState.update(state => ({
                            ...state,
                            token: newTokens.access_token,
                            expiresAt: expiresAt
                        }));

                        // Refresh booking data with new token
                        await refreshBooking();

                        if (typeof window.plausible === 'function') {
                            window.plausible('Token Refresh Success');
                        }

                        this.tokenRefreshInProgress = false;
                        return true;
                    }
                } catch (error) {
                    console.log('[AuthManager] Userscript refresh failed, trying fallback:', error.message);
                }
            }

            // Fallback: Manual refresh with mobile OAuth credentials
            let refreshToken;
            try {
                if (typeof window.getRefreshToken === 'function') {
                    refreshToken = await window.getRefreshToken();
                } else {
                    // Fallback: Try to extract refresh token from stored tokens
                    const tokens = await window.retrieveCommunautoTokens();
                    refreshToken = tokens?.refresh_token;
                }

                if (!refreshToken) {
                    console.error('No refresh token available');
                    this.tokenRefreshInProgress = false;
                    return false;
                }
            } catch (error) {
                console.error('Error retrieving refresh token:', error);
                this.tokenRefreshInProgress = false;
                return false;
            }

            // Use mobile OAuth client
            const tokenEndpoint = 'https://foidentityprovider.reservauto.net/connect/token';
            const formData = new URLSearchParams();
            formData.append('grant_type', 'refresh_token');
            formData.append('refresh_token', refreshToken);

            const response = await fetch(tokenEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept': 'application/json',
                    'Authorization': 'Basic ' + btoa('CommunautoMobileAppClient:1287F232-F5AA-4BA2-92DA-530577578820')
                },
                body: formData
            });

            if (!response.ok) {
                return false;
            }

            const data = await response.json();
            if (data.access_token) {
                console.log('Access token refreshed successfully');

                // Calculate expires_at in ms (consistent with extension storage and bookingState)
                const expiresAt = Date.now() + (data.expires_in * 1000);
                const newTokenData = {
                    ...data,
                    expires_at: expiresAt
                };

                // Update tokens in userscript storage
                try {
                    if (typeof window.updateCommunautoTokens === 'function') {
                        window.updateCommunautoTokens(newTokenData);
                    } else {
                        // Store in localStorage as a fallback - this won't be read by tampermonkey but can be
                        // useful for debugging and development
                        localStorage.setItem('temp_communauto_tokens', JSON.stringify(newTokenData));
                    }
                } catch (error) {
                    // Error updating tokens in userscript
                }

                // Update booking state with new token
                bookingState.update(state => ({
                    ...state,
                    token: data.access_token,
                    expiresAt: expiresAt
                }));

                // Refresh booking data with new token
                await refreshBooking();

                if (typeof window.plausible === 'function') {
                    window.plausible('Token Refresh Success');
                }

                return true;
            } else {
                if (typeof window.plausible === 'function') {
                    window.plausible('Token Refresh Failed', {
                        props: { error: 'Missing access_token in response' }
                    });
                }
            }
        } catch (error) {
            if (typeof window.plausible === 'function') {
                window.plausible('Token Refresh Error', {
                    props: { error: error.message }
                });
            }
        } finally {
            this.tokenRefreshInProgress = false;
        }

        return false;
    }
}
